import { Dictionary } from 'lodash';
import { DraggableLocation } from 'react-beautiful-dnd';

import { IGroup, ISimpleItem } from '../types';
import { mapGroup } from './mapGroup';

interface ICommons {
  items: Dictionary<ISimpleItem[]>;
  groups: IGroup[];
  parameterId: number;
  from: DraggableLocation;
  to: DraggableLocation;
}

const getGroupId = (droppableId: string) => droppableId.replace('group-', '');

const moveItemWithinGroup = ({ items, parameterId, from, to }: ICommons) => {
  const itemsCopy = [...items[parameterId]];
  const [movedItem] = itemsCopy.splice(from.index, 1);
  itemsCopy.splice(to.index, 0, movedItem);

  return {
    items: {
      ...items,
      [parameterId]: itemsCopy,
    },
  };
};

const moveItemFromGroup = ({
  items,
  groups,
  parameterId,
  from,
  to,
}: ICommons) => {
  const itemGroupId = getGroupId(to.droppableId);
  const itemsCopy = items[parameterId] ? [...items[parameterId]] : [];
  const [movedItem] = itemsCopy.splice(from.index, 1);
  return {
    items: {
      ...items,
      [parameterId]: itemsCopy,
    },
    groups: mapGroup({
      groups,
      parameterId,
      itemId: itemGroupId,
      callback: (itemGroup) => {
        const itemGroupEntriesCopy = [...itemGroup.items];
        itemGroupEntriesCopy.splice(to.index, 0, movedItem);
        return {
          ...itemGroup,
          items: itemGroupEntriesCopy,
        };
      },
    }),
  };
};

const moveItemToGroup = ({
  items,
  groups,
  parameterId,
  from,
  to,
}: ICommons) => {
  const itemGroupId = getGroupId(from.droppableId);
  const itemsCopy = items[parameterId] ? [...items[parameterId]] : [];
  let movedItem: ISimpleItem | undefined;

  const updatedGroups = mapGroup({
    groups,
    parameterId,
    itemId: itemGroupId,
    callback: (itemGroup) => {
      const itemGroupEntriesCopy = [...itemGroup.items];
      movedItem = itemGroupEntriesCopy.splice(from.index, 1)[0];
      return {
        ...itemGroup,
        items: itemGroupEntriesCopy,
      };
    },
  });

  if (movedItem) {
    itemsCopy.splice(to.index, 0, movedItem);
  }

  return {
    items: {
      ...items,
      [parameterId]: itemsCopy,
    },
    groups: updatedGroups,
  };
};

const moveItemBetweenGroups = ({
  items,
  groups,
  parameterId,
  from,
  to,
}: ICommons) => {
  const fromGroupId = getGroupId(from.droppableId);
  const toGroupId = getGroupId(to.droppableId);

  const possibleGroup = groups.find(
    (group) => group.parameter.id === parameterId
  );
  if (!possibleGroup) {
    return {};
  }
  const movedItemGroup = possibleGroup.itemGroups.find(
    (itemGroup) => String(itemGroup.id) === fromGroupId
  );
  if (!movedItemGroup) {
    return {};
  }
  const movedItem = movedItemGroup.items[from.index];

  const updatedGroups = groups.map((group) => {
    if (group.parameter.id !== parameterId) {
      return group;
    }
    return {
      ...group,
      itemGroups: group.itemGroups.map((itemGroup) => {
        const isFromGroup = String(itemGroup.id) === fromGroupId;
        const isToGroup = String(itemGroup.id) === toGroupId;

        if (!isFromGroup && !isToGroup) {
          return itemGroup;
        }
        const itemGroupEntriesCopy = [...itemGroup.items];
        if (isFromGroup) {
          itemGroupEntriesCopy.splice(from.index, 1);
        }
        if (isToGroup) {
          itemGroupEntriesCopy.splice(to.index, 0, movedItem);
        }
        return {
          ...itemGroup,
          items: itemGroupEntriesCopy,
        };
      }),
    };
  });

  return {
    groups: updatedGroups,
  };
};

export const moveItem = (commons: ICommons) => {
  const itemsDroppableId = 'group-items';
  const isMoveWithinItems =
    commons.from.droppableId === itemsDroppableId &&
    commons.to.droppableId === itemsDroppableId;

  if (isMoveWithinItems) {
    return moveItemWithinGroup(commons);
  }
  if (commons.from.droppableId === itemsDroppableId) {
    return moveItemFromGroup(commons);
  }
  if (commons.to.droppableId === itemsDroppableId) {
    return moveItemToGroup(commons);
  }
  return moveItemBetweenGroups(commons);
};



// WEBPACK FOOTER //
// ./src/views/Admin/ItemGroups/services/moveItem.ts